import lottie, { AnimationItem } from 'lottie-web';
import { Component, Element, Host, Method, Prop, State, Watch, h } from '@stencil/core';
import { awaitDefinedResult } from '../../utils/utils';

/** A single Lottie animation. */
@Component({
  tag: 'omk-animation',
  styleUrl: 'omk-animation.scss',
  shadow: true,
})
export class OmkAnimation {
  /** URL to fetch a Lottie formatted JSON file defining the animation. */
  @Prop() lottieDataUrl: string;
  /** Lottie JSON file contents as a string. */
  @Prop() lottieData: string;
  /** Optional caption to display above the animation. */
  @Prop() caption: string;
  /** Set play the animation as soon as it's loaded. */
  @Prop() autoplay: boolean = false;
  /** Set to repeat the animation indefinitely. Plays once by default. */
  @Prop() loop: boolean = false;

  animationContainer: HTMLDivElement;

  @State() lottieAnimation: AnimationItem;

  @Element() host: HTMLElement;

  @Watch('lottieDataUrl')
  @Watch('lottieData')
  @Watch('loop')
  @Watch('autoplay')
  async initializeAnimation() {
    this.lottieAnimation = lottie.loadAnimation({
      container: (await awaitDefinedResult(() => this.animationContainer)) as HTMLDivElement,
      renderer: 'svg',
      autoplay: this.autoplay,
      loop: this.loop,
      path: this.lottieDataUrl,
      animationData: this.lottieDataUrl ? undefined : JSON.parse(this.lottieData),
      rendererSettings: {
        preserveAspectRatio: getComputedStyle(this.animationContainer).getPropertyValue(
          '--_omk-animation-preserve-aspect-ratio',
        ),
        viewBoxSize: getComputedStyle(this.animationContainer).getPropertyValue('--_omk-animation-viewbox'),
      },
    });
  }

  /** Start the animation from the current frame. */
  @Method()
  async start() {
    this.lottieAnimation.play();
  }

  /** Stop animating and return to the first frame. */
  @Method()
  async stop() {
    this.lottieAnimation.stop();
  }

  /** Stop animating without changing the current frame. `.start()` will resume. */
  @Method()
  async pause() {
    this.lottieAnimation.pause();
  }

  connectedCallback() {
    this.initializeAnimation();
  }

  disconnectedCallback() {
    this.lottieAnimation?.destroy();
  }

  render() {
    return (
      <Host>
        <div class="caption">{this.caption}</div>
        <div
          id="animation-container"
          ref={el => {
            this.animationContainer = el;
          }}
        ></div>
      </Host>
    );
  }
}
